"use client";

import { useEffect, useCallback } from "react";
import { validateFields } from "@/lib/utils";
import { toast } from "@/hooks/useToast";
import { Section, FormState, TreeConfig } from "../types";
import {
  extractChildItemsFromResponse,
  extractParentComponentFields,
  buildDefaultFieldValues,
  prepareSectionDataForPreview,
} from "../utils";

interface UseParentComponentProps {
  selectedSection: Section;
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  treeConfig?: TreeConfig;
  parentComponentFieldsConfig: any[];
  parentComponentGetMethod?: (id: number) => Promise<any>;
  parentComponentCreateMethod?: (payload: any) => Promise<any>;
  parentComponentUpdateMethod?: (id: number, payload: any) => Promise<any>;
  fetchMethod?: (id: number) => Promise<any>;
  currentUserId?: string | number;
  pageId?: string;
  pageComponentId?: string;
}

export const useParentComponent = ({
  selectedSection,
  formState,
  setFormState,
  treeConfig,
  parentComponentFieldsConfig,
  parentComponentGetMethod,
  parentComponentCreateMethod,
  parentComponentUpdateMethod,
  fetchMethod,
  currentUserId,
  pageId,
  pageComponentId,
}: UseParentComponentProps) => {
  // Fetch parent component data
  useEffect(() => {
    const fetchParentComponent = async () => {
      const parentComponentId = selectedSection?.componentId;

      // Define data sources with priority order
      const dataSources = [
        {
          // Priority 1: Use data from selectedSection
          canUse: () =>
            selectedSection?.data &&
            typeof selectedSection.data === "object" &&
            parentComponentFieldsConfig.some((field: any) => field.fieldName in selectedSection.data!),
          getData: async () => ({
            id: selectedSection.data!.id,
            fields: extractParentComponentFields(selectedSection.data!, parentComponentFieldsConfig),
          }),
        },
        {
          // Priority 2: Fetch from API
          canUse: () =>
            parentComponentGetMethod && parentComponentId && !isNaN(Number(parentComponentId)),
          getData: async () => {
            const response = await parentComponentGetMethod!(Number(parentComponentId));
            return response?.data
              ? {
                  id: response.data.id,
                  fields: extractParentComponentFields(response.data, parentComponentFieldsConfig),
                }
              : null;
          },
        },
        {
          // Priority 3: Reset to defaults
          canUse: () => parentComponentFieldsConfig.length > 0,
          getData: async () => ({
            id: null,
            fields: buildDefaultFieldValues(parentComponentFieldsConfig),
          }),
        },
      ];

      // Execute first matching data source
      for (const source of dataSources) {
        if (source.canUse()) {
          try {
            const result = await source.getData();
            if (result) {
              setFormState((prev) => ({
                ...prev,
                parentComponentId: result.id ?? prev.parentComponentId,
                parentComponentFields: result.fields,
              }));
              return;
            }
          } catch (error) {
            console.error("Failed to fetch parent component:", error);
          }
        }
      }
    };

    fetchParentComponent();
  }, [
    selectedSection?.componentId,
    selectedSection?.id,
    selectedSection?.data,
    parentComponentGetMethod,
    parentComponentFieldsConfig,
    setFormState,
  ]);

  // Handler for parent component field updates
  const handleParentComponentFieldsUpdate = useCallback(
    (updatedData: Record<string, any>) => {
      setFormState((prev) => ({ ...prev, parentComponentFields: updatedData }));
    },
    [setFormState]
  );

  // Save parent component
  const handleParentComponentSave = useCallback(
    async (onSave: (updatedSection: Section) => void) => {
      if (!pageId || !pageComponentId) {
        toast({
          title: "Error",
          description: "Page ID and Component ID are required",
          variant: "destructive",
        });
        return;
      }

      const { errors: fieldErrors } = validateFields(
        parentComponentFieldsConfig,
        formState.parentComponentFields
      );

      setFormState((prev) => ({ ...prev, parentComponentFieldErrors: fieldErrors }));

      if (Object.keys(fieldErrors).length > 0) {
        toast({
          title: "Validation Error",
          description: "Please fix the errors before saving",
          variant: "destructive",
        });
        return;
      }

      try {
        const parentComponentIdToUse = selectedSection?.componentId || formState.parentComponentId;
        const childItemsFieldName = treeConfig?.childItemsFieldName || "menuItems";
        const isUpdate = Boolean(parentComponentIdToUse && parentComponentUpdateMethod);

        // Define save operations using object mapping
        const saveOperations = {
          update: {
            canExecute: () => Boolean(parentComponentIdToUse && parentComponentUpdateMethod),
            getPayload: () => ({
              ...formState.parentComponentFields,
              page_id: Number(pageId),
              updated_by: currentUserId,
            }),
            execute: async (payload: any) => {
              await parentComponentUpdateMethod!(Number(parentComponentIdToUse), payload);
              return parentComponentIdToUse;
            },
            successMessage:
              treeConfig?.parentComponentUpdateSuccessMessage ||
              "Parent component settings updated successfully",
          },
          create: {
            canExecute: () => Boolean(parentComponentCreateMethod),
            getPayload: () => ({
              ...formState.parentComponentFields,
              page_id: Number(pageId),
              component_id: Number(pageComponentId),
              created_by: currentUserId,
            }),
            execute: async (payload: any) => {
              const response = await parentComponentCreateMethod!(payload);
              const newId = response?.data?.id || response?.id;
              if (newId) {
                setFormState((prev) => ({ ...prev, parentComponentId: newId }));
              }
              return newId;
            },
            successMessage:
              treeConfig?.parentComponentCreateSuccessMessage ||
              "Parent component created successfully",
          },
        };

        const operation = isUpdate ? saveOperations.update : saveOperations.create;

        if (!operation.canExecute()) return;

        const payload = operation.getPayload();
        const resultId = await operation.execute(payload);

        // Fetch and update preview
        if (resultId && fetchMethod) {
          const response = await fetchMethod(Number(resultId));
          const { items, parentComponentData } = extractChildItemsFromResponse(
            response,
            childItemsFieldName
          );

          onSave(
            prepareSectionDataForPreview(
              selectedSection,
              resultId,
              parentComponentData,
              formState.parentComponentFields,
              items,
              childItemsFieldName,
              pageId,
              pageComponentId
            )
          );
        }

        toast({
          title: "Success",
          description: operation.successMessage,
        });
      } catch (error: any) {
        console.error("Failed to save parent component:", error);
        toast({
          title: "Error",
          description: error?.message || "Failed to save parent component settings",
          variant: "destructive",
        });
      }
    },
    [
      pageId,
      pageComponentId,
      parentComponentFieldsConfig,
      formState.parentComponentFields,
      formState.parentComponentId,
      selectedSection,
      treeConfig,
      currentUserId,
      parentComponentUpdateMethod,
      parentComponentCreateMethod,
      fetchMethod,
      setFormState,
    ]
  );

  return {
    handleParentComponentFieldsUpdate,
    handleParentComponentSave,
  };
};

